'use client';

import { useState, useRef, useEffect } from 'react';
import { contentApi } from '@/lib/api';
import { Clapperboard, Maximize2, Minimize2, RefreshCw, ExternalLink, Send, Loader2, Sparkles, ChevronDown } from 'lucide-react';
import { useContent } from '@/contexts/ContentContext';

const STUDIO_BASE_URL = 'http://localhost:3100';

interface CompositionInfo {
  id: string;
  folder: string;
  project?: string;
  title?: string;
}

export default function RemotionStudio() {
  const { selectedProjectSlug } = useContent();
  const [expanded, setExpanded] = useState(false);
  const [key, setKey] = useState(0);
  const [input, setInput] = useState('');
  const [editing, setEditing] = useState(false);
  const [editPhase, setEditPhase] = useState('');
  const [lastEdit, setLastEdit] = useState('');
  const [compositions, setCompositions] = useState<CompositionInfo[]>([]);
  const [activeCompositionId, _setActiveCompositionId] = useState('');
  const setActiveCompositionId = (id: string) => {
    _setActiveCompositionId(id);
    if (id) localStorage.setItem('studio-active-composition', id);
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const phaseInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    contentApi.compositions()
      .then(data => {
        const comps: CompositionInfo[] = data.compositions || [];
        setCompositions(comps);
        if (comps.length === 0) return;
        const saved = localStorage.getItem('studio-active-composition');
        if (saved && comps.find(c => c.id === saved)) {
          _setActiveCompositionId(saved);
          hasInitialized.current = true;
          return;
        }
        if (selectedProjectSlug) {
          const match = comps.find(c => c.project === selectedProjectSlug);
          if (match) {
            setActiveCompositionId(match.id);
            hasInitialized.current = true;
            return;
          }
        }
        setActiveCompositionId(comps[0].id);
        hasInitialized.current = true;
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevSlugRef = useRef(selectedProjectSlug);
  useEffect(() => {
    if (!hasInitialized.current) return;
    if (selectedProjectSlug === prevSlugRef.current) return;
    prevSlugRef.current = selectedProjectSlug;
    if (!selectedProjectSlug || compositions.length === 0) return;
    const match = compositions.find(c => c.project === selectedProjectSlug);
    if (match && match.id !== activeCompositionId) {
      setActiveCompositionId(match.id);
      setKey(k => k + 1);
    }
  }, [selectedProjectSlug, compositions, activeCompositionId]);

  const studioUrl = activeCompositionId
    ? `${STUDIO_BASE_URL}/${activeCompositionId}`
    : STUDIO_BASE_URL;

  useEffect(() => {
    if (editing) {
      const phases = [
        '✨ Understanding your request...',
        '🎬 Editing scenes...',
        '⚡ Applying changes...',
        '🔄 Almost there...',
      ];
      let i = 0;
      setEditPhase(phases[0]);
      phaseInterval.current = setInterval(() => {
        i = (i + 1) % phases.length;
        setEditPhase(phases[i]);
      }, 3000);
      return () => {
        if (phaseInterval.current) clearInterval(phaseInterval.current);
      };
    } else {
      setEditPhase('');
    }
  }, [editing]);

  const handleSubmit = async () => {
    if (!input.trim() || editing) return;
    const prompt = input.trim();
    setInput('');
    setEditing(true);
    setLastEdit('');
    try {
      const data = await contentApi.studioEdit({ prompt, compositionId: activeCompositionId });
      if (data.ok) {
        setLastEdit(data.summary?.slice(0, 120) || 'Done');
        setTimeout(() => setKey(k => k + 1), 1500);
      } else {
        setLastEdit(`⚠️ ${data.error || 'Edit failed'}`);
      }
    } catch {
      setLastEdit('⚠️ Could not reach the edit server');
    } finally {
      setEditing(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className={`flex flex-col ${expanded ? 'fixed inset-0 z-50 bg-black' : 'h-full'}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-black/60 backdrop-blur shrink-0">
        <div className="flex items-center gap-3">
          <Clapperboard className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-semibold text-white">Remotion Studio</span>
          {compositions.length > 1 && (
            <div className="relative">
              <select
                value={activeCompositionId}
                onChange={(e) => {
                  setActiveCompositionId(e.target.value);
                  setKey(k => k + 1);
                }}
                className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs text-cyan-300 appearance-none cursor-pointer pr-6 focus:border-cyan-500/50 focus:outline-none"
              >
                {compositions.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.title || c.id}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-3 h-3 text-gray-500 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setKey(k => k + 1)} className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-colors" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => setExpanded(!expanded)} className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-colors" title={expanded ? 'Exit Fullscreen' : 'Fullscreen'}>
            {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <a href={studioUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-colors" title="Open in new tab">
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* iframe */}
      <div className="flex-1 relative min-h-0">
        <iframe
          key={`${key}-${activeCompositionId}`}
          src={studioUrl}
          className="absolute inset-0 w-full h-full border-0"
          allow="autoplay; fullscreen"
          title="Remotion Studio"
        />
      </div>

      {/* Edit bar */}
      <div className="shrink-0 border-t border-white/10 bg-black/80 backdrop-blur px-4 py-3">
        {(editing || lastEdit) && (
          <div className="mb-2 flex items-center gap-2">
            {editing ? (
              <div className="flex items-center gap-2 text-cyan-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="text-xs font-medium animate-pulse">{editPhase}</span>
              </div>
            ) : lastEdit ? (
              <span className="text-xs text-gray-500">✓ {lastEdit}</span>
            ) : null}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-cyan-500/50 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            placeholder={editing ? 'Editing in progress...' : 'Tell me what to change...'}
            disabled={editing}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-cyan-500/50 focus:outline-none disabled:opacity-50 transition-colors"
          />
          <button
            onClick={handleSubmit}
            disabled={!input.trim() || editing}
            className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {editing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
